import {
  UserInterface,
  UserPropKey,
  UserVarKey,
  UserVarsMap,
} from "@swivl-mono/types";
import { Input } from "@swivl-mono/ui";
import { useEffect, useState, ChangeEvent } from "react";

import LocationsAndGroupsDropdown, {
  LocationsAndGroupsDropdownType,
} from "../../../../Components/Dropdowns/LocationsAndGroupsDropdown/LocationsAndGroupsDropdown";
import UserVarsModel from "../../../../Models/Variables/UserVars.Model";
import { UsersModel } from "../../../../Models/Users/Users.Model";
import { AlertsModel } from "../../../../Models/Notifications/Alerts.Model";

interface UserDetailsUserVarsComponentProps {
  user: UserInterface;
  userVars: UserVarsMap;
}

const userVarKeySet = new Set(Object.values(UserVarKey));

export function UserDetailsUserVarsComponent({ user, userVars }: UserDetailsUserVarsComponentProps) {
  const [localValues, setLocalValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setLocalValues({
      ...userVars,
      ...Object.entries(user).reduce((acc, [key, val]) => {
        if (typeof val === "string" || typeof val === "number") {
          acc[key] = String(val);
        }
        return acc;
      }, {} as Record<string, string>),
    });
  }, [userVars, user]);

  const checkIfPhoneNumberExists = async (phoneNumber: string) => {
    const users = await UsersModel.getUsersByPhoneNumber(phoneNumber);
    return users.length > 0 && users[0].id !== user.id;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, key: string) => {
    setLocalValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleBlur = async (key: string) => {
    const value = localValues[key];
    if (!user?.id || !key || !value) return;

    // phone check
    if (key === "phoneNumber" || key === "phoneNumbers") {
      const phoneNumberArray =
        key === "phoneNumber"
          ? [value]
          : value
            .split(",")
            .map((num) => num.trim())
            .filter(Boolean);

      const existentNumbers = await Promise.all(
        phoneNumberArray.map(checkIfPhoneNumberExists)
      );

      if (existentNumbers.some(Boolean)) {
        AlertsModel.errorToast("One or more phone numbers already exist");
        return;
      }
    }

    const isUserVarKey = userVarKeySet.has(key as UserVarKey);

    // strip undefined values
    const parsedUser = Object.fromEntries(
      Object.entries(user).map(([key, v]) => [key, v ? v : undefined])
    );

    if (isUserVarKey) {
      await UserVarsModel.saveUserVars(user.id, {
        ...userVars,
        [key]: value,
      });
    } else {
      await UsersModel.updateUser({
        ...parsedUser,
        [key]: value,
      });
    }
  };

  return (
    localValues &&
    Object.keys(userVars).map((userVar) => {
      const isLocationDropdown =
        (userVar as UserVarKey) === UserVarKey.Location;

      return (
        <div key={userVar}>
          <label>{`USER - ${userVar?.toUpperCase()}`}</label>
          {isLocationDropdown ? (
            <LocationsAndGroupsDropdown
              locationId={parseInt(localValues[userVar])}
              type={LocationsAndGroupsDropdownType.LOCATIONS}
              onSelect={(groupId: number, locationId: number) => {
                setLocalValues((prev) => ({
                  ...prev,
                  [userVar]: String(locationId),
                }));
                handleBlur(userVar);
              }}
            />
          ) : (
            <Input
              value={localValues[userVar] || ""}
              onChange={(e) => handleChange(e, userVar)}
              onBlur={() => handleBlur(userVar)}
              enableAutoComplete={false}
            />
          )}
        </div>
      );
    })
  );
}
